import classNames from 'classnames';
import React, { FC, memo, useMemo, ReactNode } from 'react';
import { Trans } from 'react-i18next';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { GroupedSizeCode, SizeCodeForBooking, SizeCodeType } from '../model/types';
import { Popup } from 'shared/ui/Popup';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';

interface ModernSizeGroupsTagsProps {
  sizeCodes: SizeCodeForBooking[] | undefined;
  showTooltip?: boolean;
}

type GroupedSizeCodeExtended = GroupedSizeCode & {
  hasCubicBox: boolean;

  minCubicSquare?: number;
  maxCubicSquare?: number;

  minRegularSquare?: number;
  maxRegularSquare?: number;
};

export const ModernSizeGroupsTags: FC<ModernSizeGroupsTagsProps> = memo((props) => {
  const { sizeCodes, showTooltip = true } = props;

  const { t } = useAppTranslation('booking');

  const groupedBySizeGroups = useMemo<GroupedSizeCodeExtended[] | undefined>(
    () =>
      sizeCodes?.reduce((acc: GroupedSizeCodeExtended[], sizeCode) => {
        const existingGroup = acc.find((item) => item.sizeGroup === sizeCode.sizeGroup);

        const isCubic = sizeCode.sizeCodeType === SizeCodeType.CUBIC_BOX;

        if (existingGroup) {
          existingGroup.hasCubicBox = existingGroup.hasCubicBox || isCubic;

          // required for GroupedSizeCode
          existingGroup.minBoxSquare = Math.min(existingGroup.minBoxSquare ?? Infinity, sizeCode.square);

          existingGroup.maxBoxSquare = Math.max(existingGroup.maxBoxSquare ?? -Infinity, sizeCode.square);

          // separate cubic / regular logic
          if (isCubic) {
            existingGroup.minCubicSquare = Math.min(existingGroup.minCubicSquare ?? Infinity, sizeCode.square);

            existingGroup.maxCubicSquare = Math.max(existingGroup.maxCubicSquare ?? -Infinity, sizeCode.square);
          } else {
            existingGroup.minRegularSquare = Math.min(existingGroup.minRegularSquare ?? Infinity, sizeCode.square);

            existingGroup.maxRegularSquare = Math.max(existingGroup.maxRegularSquare ?? -Infinity, sizeCode.square);
          }

          existingGroup.availableBoxesCount += sizeCode.availableBoxesCount;
        } else {
          acc.push({
            ...sizeCode,

            // required fields
            minBoxSquare: sizeCode.square,
            maxBoxSquare: sizeCode.square,

            hasCubicBox: isCubic,

            // cubic sizes
            minCubicSquare: isCubic ? sizeCode.square : undefined,

            maxCubicSquare: isCubic ? sizeCode.square : undefined,

            // regular sizes
            minRegularSquare: !isCubic ? sizeCode.square : undefined,

            maxRegularSquare: !isCubic ? sizeCode.square : undefined,
          });
        }

        return acc;
      }, []),
    [sizeCodes],
  );

  if (!groupedBySizeGroups || groupedBySizeGroups.length === 0) {
    return null;
  }

  const renderSizeTag = (sizeCode: GroupedSizeCodeExtended): ReactNode => {
    const sizeTag = (
      <div
        className={classNames(
          'flex justify-center items-center text-white text-13 font-normal font-tektur bg-primary rounded-md w-9 h-9 cursor-pointer',
          {
            'opacity-30': sizeCode.availableBoxesCount < 1,
          },
        )}
      >
        {sizeCode.sizeGroup}
      </div>
    );

    if (!showTooltip) {
      return sizeTag;
    }

    const description =
      sizeCode.availableBoxesCount > 0 ? (
        (() => {
          const hasCubic = sizeCode.minCubicSquare != null;

          const hasRegular = sizeCode.minRegularSquare != null;

          // mixed category
          if (hasCubic && hasRegular) {
            return t('This category includes boxes from {{min}} m³ to {{max}} m²', {
              min: formatDecimal(sizeCode.minCubicSquare),
              max: formatDecimal(sizeCode.maxRegularSquare),
            });
          }

          // cubic only
          if (hasCubic) {
            return t('This category includes boxes from {{min}} m³ to {{max}} m³', {
              min: formatDecimal(sizeCode.minCubicSquare),
              max: formatDecimal(sizeCode.maxCubicSquare),
            });
          }

          // regular only
          return t('This category includes boxes from {{min}} m² to {{max}} m²', {
            min: formatDecimal(sizeCode.minRegularSquare),
            max: formatDecimal(sizeCode.maxRegularSquare),
          });
        })()
      ) : (
        <Trans
          t={t}
          i18nKey="<0>All boxes in this category are occupied!</0> <1>Please select another category or look for a</1> <2>box suitable for you in another warehouse</2>"
          components={[<div key="0" />, <div key="1" />, <div key="2" />]}
        />
      );

    return (
      <Popup
        title={t('Box size {{sizeGroup}}', {
          sizeGroup: sizeCode.sizeGroup,
        })}
        description={description}
        color="var(--accent)"
        textColor="var(--white)"
      >
        {sizeTag}
      </Popup>
    );
  };

  return (
    <div>
      <div className="flex flex-row space-x-2">
        {groupedBySizeGroups.map((sizeCode) => (
          <div key={sizeCode.sizeCodeId}>{renderSizeTag(sizeCode)}</div>
        ))}
      </div>
    </div>
  );
});
